import type { StatusTone } from '@/lib/status-tones'
import type { ManufacturerPaymentTerms } from '@/features/manufacturers/payment-terms-panel'
import { describePaymentTerms } from '@/features/manufacturers/payment-terms'
import type { SupplierKind } from '@/api/sourcing'

/** Supplier kinds, told apart by color in lists (USA-1535). */
export const SUPPLIER_KIND_TONES: Record<SupplierKind, StatusTone> = {
  rep_group: 'progress',
  distributor: 'success',
  online_retailer: 'warning',
  buying_group: 'neutral',
}

type AccountStatus = 'open' | 'pending' | 'opening_order_needed' | 'not_opened' | 'not_required' | 'unset'

export const ACCOUNT_LABELS: Record<AccountStatus, string> = {
  open: 'Account open',
  pending: 'Account pending',
  opening_order_needed: 'Needs opening order',
  not_opened: 'No account',
  not_required: 'No account needed',
  unset: 'Account not recorded',
}

/** Short forms for a supplier's account tally ("7 open"). */
export const ACCOUNT_SHORT_LABELS: Record<AccountStatus, string> = {
  open: 'open',
  pending: 'pending',
  opening_order_needed: 'need opening order',
  not_opened: 'no account',
  not_required: 'no account needed',
  unset: 'not recorded',
}

export const ACCOUNT_TONES: Record<AccountStatus, StatusTone> = {
  open: 'success',
  pending: 'progress',
  opening_order_needed: 'warning',
  not_opened: 'danger',
  not_required: 'neutral',
  unset: 'neutral',
}

export const ACCOUNT_ORDER: AccountStatus[] = [
  'open',
  'pending',
  'opening_order_needed',
  'not_opened',
  'not_required',
  'unset',
]

export function accountStatus(value: string | null | undefined): AccountStatus {
  return value && value in ACCOUNT_LABELS ? (value as AccountStatus) : 'unset'
}

/** Payment terms as a short label and tone: paying up front stands out (who to ask for terms). */
export function termsPill(terms: ManufacturerPaymentTerms): { label: string; tone: StatusTone } {
  const label = describePaymentTerms(terms)
  const deposit = terms.payment_terms_deposit_percent != null && terms.payment_terms_deposit_percent !== ''
  if (terms.payment_terms_type === 'prepaid') return { label: 'CIA / Prepaid', tone: 'warning' }
  if (deposit || terms.payment_terms_type === 'due_on_receipt') return { label, tone: 'warning' }
  if (!terms.payment_terms_type || terms.payment_terms_type === 'unknown') {
    return { label: 'No terms', tone: 'neutral' }
  }
  return { label, tone: 'success' }
}

export const SUPPLIER_ROLE_LABELS: Record<string, string> = {
  po_primary: 'Orders',
  po_primary_cc: 'Orders CC',
  spec_credit: 'Spec credit',
  spec_credit_cc: 'Spec credit CC',
  accounting: 'Accounting',
  customer_service: 'Customer service',
  service: 'Service',
}

export const SUPPLIER_ROLES = Object.keys(SUPPLIER_ROLE_LABELS)
